import { Box, Center, Flex, IconButton, Spinner, Stack, Text, Tooltip } from "@chakra-ui/react";
import { ConnectorType, v2 } from "@pufflig/prompt-studio-types";
import { ConnectorIcon, Icon, SchemaOutline } from "@pufflig/prompt-studio-ui";
import { refreshSchema } from "../state/actions/refreshSchema";
import { toggleVisibility } from "../state/actions/toggleVisibility";
import { useSemanticModel } from "../state/useSemanticModel";

const connectorName: Record<ConnectorType, string> = {
  amplitude: "Amplitude",
  bigquery: "BigQuery",
  ms_fabric_sql: "Microsoft Fabric",
  postgres: "Postgres",
  posthog: "Posthog",
  salesforce: "Salesforce",
  snowflake: "Snowflake",
};

interface DataSchemaSidebarProps {
  connector: v2.ConnectorResource | null;
  onClose?: () => void;
  onOpenYaml?: () => void;
  onSelectTable?: (schema: string, table: string) => void;
}

export const DataSchemaSidebar = ({ connector, onClose, onSelectTable, onOpenYaml }: DataSchemaSidebarProps) => {
  const semanticModel = useSemanticModel((s) => s.semanticModel);
  const isLoading = useSemanticModel((s) => s.isLoadingSchema);

  const handleRefresh = () => {
    if (!connector) return;
    refreshSchema(connector.resourceId);
  };

  return (
    <Flex direction={"column"} width="260px" borderRight="1px solid" borderColor="border.secondary" overflow="hidden">
      <Flex
        borderBottom="1px solid"
        borderColor="border.secondary"
        paddingTop="spacing-lg"
        paddingBottom="spacing-md"
        paddingX="spacing-md"
      >
        {onClose && (
          <Tooltip label="Back to Data Sources">
            <IconButton
              mt="-1px"
              marginRight="spacing-xxs"
              variant="ghost"
              size="xs"
              aria-label="Back"
              icon={<Icon name="arrow-left-2" size="sm" />}
              onClick={onClose}
            />
          </Tooltip>
        )}
        <Stack flex="1" gap="spacing-xxs">
          <Flex gap="spacing-xxs">
            <Box>
              <Text noOfLines={1} textStyle="label/M/medium">
                {connector?.name}
              </Text>
            </Box>
            {onOpenYaml && (
              <Box>
                <Tooltip label="Edit YAML">
                  <IconButton
                    aria-label="Edit YAML"
                    size="sm"
                    variant="ghost"
                    icon={<Icon size="sm" name="edit" />}
                    onClick={onOpenYaml}
                  />
                </Tooltip>
              </Box>
            )}
          </Flex>
          <Text textStyle="label/S/regular" color="foreground.secondary">
            {connector?.description}
          </Text>
          <Flex flex="1" justifyContent="space-between" mt="spacing-xs">
            <Flex gap="spacing-xs" alignItems="center">
              <ConnectorIcon size="sm" connectorType={connector?.connectorType || "unknown"} />
              <Text textStyle="label/M/regular" color="foreground.secondary">
                {connectorName[connector?.connectorType || ""] || "Unknown"}
              </Text>
            </Flex>
            <Tooltip label="Refresh">
              <IconButton
                isLoading={isLoading}
                onClick={handleRefresh}
                variant="ghost"
                size="sm"
                aria-label="Refresh"
                icon={<Icon name="refresh-1" size="sm" />}
              />
            </Tooltip>
          </Flex>
        </Stack>
      </Flex>
      <Flex flex="1" overflow="auto">
        {isLoading && (
          <Box padding="spacing-lg">
            <Center>
              <Spinner />
            </Center>
          </Box>
        )}
        {!isLoading && semanticModel && (
          <SchemaOutline
            onHideItem={(id) => {
              if (!connector) return;
              toggleVisibility(id);
            }}
            schemas={semanticModel.schemas || {}}
            onSelectTable={onSelectTable}
          />
        )}
      </Flex>
    </Flex>
  );
};
